import type { AudioSourceData } from '@lumen5/luminary';

import type {
    AssetContainerModel,
    AudioAssetModel,
    ClickLogsClickRequest,
    ClickLogsQuery,
    ClickLogsQueryRequest,
    ClickLogsRecommendation,
    ClickLogsRecommendationRequest,
    ClickLogsResult,
} from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    AssetTypes,
    SlideBackgroundModelTypeEnum,
} from 'js/src/client';
import { getVideoIdOrThrow, getVideoLanguageOrThrow } from 'js/src/data/app';
import { CLICK_LOG_TYPES, SITE_SEARCH_TYPES } from 'js/src/data/click-logs/click-logs-types';
import type { SearchContextValueType, SearchResponse } from 'js/src/data/media';
import type { MediaFilter } from 'js/src/data/media/cookie-operations';
import {
    getActiveSearchContextForCurrentTab,
    getActiveSearchTerm,
    getRecommendedSearchTermsForSearchContext,
} from 'js/src/data/media/media-selectors';
import {
    immutableSlideBackgroundModelIsImmutableImageBackgroundModel,
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
    SEARCH_CONTEXT,
    slideBackgroundModelIsImageBackgroundModel,
    slideBackgroundModelIsVideoBackgroundModel,
} from 'js/src/data/media/media-types';
import { checkAssetUUID } from 'js/src/data/media/media-utils';
import {
    assetContainerModelIsAudio,
    assetContainerModelIsBackground,
    assetContainerModelIsIcon,
    immutableAssetContainerModelIsBackground,
    immutableAssetContainerModelIsIcon,
} from 'js/src/data/uploads';
import type { ImmutableMap } from 'js/src/libs/consts';
import PremiumMediaChecker, { PREMIUM_LEVEL } from 'js/src/libs/premium-media-checker';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

type LogParams = {
    log_name?: string;
    results_shape?: [number, number];
    filter_state?: { [k: string]: unknown };
};

function logQuery(
    video_id: number,
    query: string,
    corrected_query: string,
    processed_query: string,
    page: number,
    results: ClickLogsQuery['results'],
    language: string,
    params: LogParams = {},
) {
    const log_query: ClickLogsQueryRequest = {
        video_id,
        query,
        corrected_query,
        processed_query,
        page,
        results,
        language,
    };
    const { log_name = null, results_shape = null, filter_state = null } = params;
    if (log_name !== null) {
        log_query['log_name'] = log_name;
    }
    if (results_shape !== null) {
        log_query['results_shape'] = results_shape;
    }
    if (filter_state !== null) {
        log_query['filter'] = filter_state;
    }
    return (dispatch: AppDispatch) => {
        ApiService.clicklogsqueryCreate({ body: log_query });
    };
}

function logRecommendation(
    video_id: number,
    query: string[],
    strategy: string,
    page: number,
    results: ClickLogsRecommendation['results'],
    language: string,
    params: LogParams = {},
) {
    const log_query: ClickLogsRecommendationRequest = {
        video_id,
        query,
        strategy,
        page,
        results,
        language,
    };
    const { log_name = null, results_shape = null, filter_state = null } = params;
    if (log_name !== null) {
        log_query['log_name'] = log_name;
    }
    if (results_shape !== null) {
        log_query['results_shape'] = results_shape;
    }
    if (filter_state !== null) {
        log_query['filter'] = filter_state;
    }
    return (dispatch: AppDispatch) => {
        ApiService.clicklogsrecommendationCreate({ body: log_query });
    };
}

function logAction(action_query: ClickLogsClickRequest) {
    return (dispatch: AppDispatch) => {
        return ApiService.clicklogsclickCreate({ body: action_query });
    };
}

function logClick(
    video_id: number,
    result: ClickLogsResult,
    language: string,
    params: Record<string, string> = {},
) {
    const action = 'C';
    const click_query: ClickLogsClickRequest = {
        video_id,
        action,
        result,
        language,
    };
    const { log_name = null } = params;
    if (log_name !== null) {
        click_query['log_name'] = log_name;
    }
    return logAction(click_query);
}

function logDrag(
    video_id: number,
    result: ClickLogsResult,
    language: string,
    params: Record<string, string> = {},
) {
    const action = 'D';
    const drag_query: ClickLogsClickRequest = {
        video_id,
        action,
        result,
        language,
    };
    const { log_name = null } = params;
    if (log_name !== null) {
        drag_query['log_name'] = log_name;
    }
    return logAction(drag_query);
}

function logApply(
    video_id: number,
    result: ClickLogsResult,
    language: string,
    params: Record<string, string> = {},
) {
    const action = 'A';
    const drag_query: ClickLogsClickRequest = {
        video_id,
        action,
        result,
        language,
    };
    const { log_name = null } = params;
    if (log_name !== null) {
        drag_query['log_name'] = log_name;
    }
    return logAction(drag_query);
}

function getClickLogInfo(asset: ImmutableMap<AssetContainerModel>) {
    let assetType: AssetTypes | null = null;
    let source: number | null = null;
    let uuid: string | null = null;
    if (immutableAssetContainerModelIsBackground(asset)) {
        const background = asset.get(AssetContainerTypes.BACKGROUND);
        if (immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(background)) {
            assetType = AssetTypes.VIDEO;
            source = background.get(SlideBackgroundModelTypeEnum.VIDEO).get('source');
            uuid = background.get(SlideBackgroundModelTypeEnum.VIDEO).get('uuid');
        } else if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(background)) {
            assetType = AssetTypes.IMAGE;
            source = background.get(SlideBackgroundModelTypeEnum.IMAGE).get('source');
            uuid = background.get(SlideBackgroundModelTypeEnum.IMAGE).get('uuid');
        }
    } else if (immutableAssetContainerModelIsIcon(asset)) {
        assetType = AssetTypes.ICON;
        source = asset.get(AssetContainerTypes.ICON).get('source');
        uuid = asset.get(AssetContainerTypes.ICON).get('uuid');
    }
    if (assetType === null || source === null || uuid === null) {
        throw new Error('Unknown asset type when logging apply from background tab');
    }
    return {
        assetType,
        source,
        uuid,
    };
}

export function logSearch(
    response: SearchResponse,
    search_term: string,
    processed_search_term: string,
    page: number,
    search_context: SearchContextValueType,
    is_audio_search: boolean,
    filter: MediaFilter[],
    corrected_search_term: string,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        // log the query to our analytics services
        let log_name = null;
        let virtual_page = null;

        if (search_term === '' && search_context === SEARCH_CONTEXT.RECOMMENDED_VISUALS) {
            log_name = CLICK_LOG_TYPES.MEDIA_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.MEDIA_RECOMMENDATIONS;
        } else if (search_term === '' && search_context === SEARCH_CONTEXT.STOCK_AUDIO) {
            log_name = CLICK_LOG_TYPES.AUDIO_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.AUDIO_RECOMMENDATIONS;
        } else if (search_term === '' && search_context === SEARCH_CONTEXT.RECENT_VISUALS) {
            log_name = CLICK_LOG_TYPES.RECENT_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.RECENT_RECOMMENDATIONS;
        } else if (search_term === '' && search_context === SEARCH_CONTEXT.FAVORITE_VISUALS) {
            log_name = CLICK_LOG_TYPES.FAVORITE_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.FAVORITE_RECOMMENDATIONS;
        } else if (search_term === '' && search_context === SEARCH_CONTEXT.UPLOADED_VISUALS) {
            log_name = CLICK_LOG_TYPES.UPLOADS_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.UPLOADS_RECOMMENDATIONS;
        } else
            switch (search_context) {
                case SEARCH_CONTEXT.RECOMMENDED_VISUALS: {
                    log_name = CLICK_LOG_TYPES.MEDIA_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.MEDIA_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.STOCK_AUDIO: {
                    log_name = CLICK_LOG_TYPES.AUDIO_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.AUDIO_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.RECENT_VISUALS: {
                    log_name = CLICK_LOG_TYPES.RECENT_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.RECENT_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.FAVORITE_VISUALS: {
                    log_name = CLICK_LOG_TYPES.FAVORITE_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.FAVORITE_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.UPLOADED_VISUALS: {
                    log_name = CLICK_LOG_TYPES.UPLOADS_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.UPLOADS_SEARCH;

                    break;
                }
                // No default
            }

        if (log_name) {
            const state = getState();
            const filter_state = { active: filter };
            const params: LogParams = {
                log_name,
                filter_state,
            };
            if (is_audio_search === true) {
                params.results_shape = [-1, 1];
            }

            if (corrected_search_term === '') {
                corrected_search_term = search_term;
            }
            const { search_source = 'recommended' } = response;
            const query = getRecommendedSearchTermsForSearchContext(state, search_context)
                .toJS()
                .splice(0, 5);
            const typeAndUuids = response.results.map((result) => {
                let uuid: string | null = null;
                let assetType: AssetTypes | null = null;
                if (assetContainerModelIsAudio(result)) {
                    uuid = result.audio.uuid;
                    assetType = AssetTypes.AUDIO;
                } else if (assetContainerModelIsBackground(result)) {
                    const background = result[AssetContainerTypes.BACKGROUND];
                    if (slideBackgroundModelIsVideoBackgroundModel(background)) {
                        uuid = background.video.uuid;
                        assetType = AssetTypes.VIDEO;
                    } else if (slideBackgroundModelIsImageBackgroundModel(background)) {
                        uuid = background.image.uuid;
                        assetType = AssetTypes.IMAGE;
                    }
                } else if (assetContainerModelIsIcon(result)) {
                    uuid = result[AssetContainerTypes.ICON].uuid;
                    assetType = AssetTypes.ICON;
                }

                if (!assetType || !uuid) {
                    throw new Error('Unknown asset type or uuid');
                }

                return {
                    type: assetType,
                    uuid: checkAssetUUID(uuid),
                };
            });
            if (search_term === '') {
                dispatch(
                    logRecommendation(
                        getVideoIdOrThrow(state),
                        query,
                        search_source,
                        page,
                        typeAndUuids,
                        getVideoLanguageOrThrow(state),
                        params,
                    ),
                );
            } else {
                dispatch(
                    logQuery(
                        getVideoIdOrThrow(state),
                        search_term,
                        corrected_search_term,
                        processed_search_term,
                        page,
                        typeAndUuids,
                        getVideoLanguageOrThrow(state),
                        params,
                    ),
                );
            }

            // no site search for recommendation panel events
            const notBlankSearch = search_term !== '';
            if (notBlankSearch) {
                if (virtual_page != null && page === 1) {
                    const escaped_search_term = search_term.replace(/&/g, 'and');
                    analytics.trackSearch(
                        `/${virtual_page}/search?q=${encodeURIComponent(escaped_search_term)}`,
                    );
                }
            }
        }

        // if there are no search results, log this to mixpanel so we can work to improve results
        const notBlankSearch = search_term !== '';
        if (notBlankSearch) {
            const hasNoResults = response.results.length === 0 && page === 1;
            if (hasNoResults) {
                analytics.track('Media Search: No Results Found For Search', {
                    search_term,
                    source: search_context,
                });
            }
        }
    };
}

export function logClickFromAudioTab(track: ImmutableMap<AudioAssetModel | AudioSourceData>) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const searchContext = getActiveSearchContextForCurrentTab(getState());
        if (searchContext === SEARCH_CONTEXT.STOCK_AUDIO) {
            let log_name = CLICK_LOG_TYPES.AUDIO_SEARCH;
            if (getActiveSearchTerm(getState(), searchContext) === '') {
                log_name = CLICK_LOG_TYPES.AUDIO_RECOMMENDATIONS;
            }
            const result_type = AssetTypes.AUDIO;
            dispatch(
                logClick(
                    getVideoIdOrThrow(getState()),
                    {
                        type: result_type,
                        uuid: checkAssetUUID(track.get('uuid')),
                    },
                    getVideoLanguageOrThrow(getState()),
                    {
                        log_name,
                    },
                ),
            );
        }
    };
}

export function logClickFromBackgroundTab(
    asset: ImmutableMap<AssetContainerModel>,
    media_asset_index: number,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const search_context = getActiveSearchContextForCurrentTab(getState());
        if (!search_context) throw new Error('No search context found for current tab');
        const active_media_search_term = getActiveSearchTerm(getState(), search_context);

        // if asset requires an upgrade it's an upsell so don't log
        const { assetType, source, uuid } = getClickLogInfo(asset);
        const badge = PremiumMediaChecker.badge_required_for_source(
            source,
            assetType === AssetTypes.ICON
                ? AssetContainerTypes.ICON
                : AssetContainerTypes.BACKGROUND,
        );
        if (
            badge === PREMIUM_LEVEL.UPGRADE_FOR_MARKETPLACE ||
            badge === PREMIUM_LEVEL.UPGRADE_FOR_UNLIMITED
        ) {
            return;
        }

        let log_name = null;
        let virtual_page = null;
        if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.RECOMMENDED_VISUALS
        ) {
            // if search term is blank, it's not a keyword search
            // so log as recommendations panel
            log_name = CLICK_LOG_TYPES.MEDIA_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.MEDIA_RECOMMENDATIONS;
        } else if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.RECENT_VISUALS
        ) {
            // if search term is blank, it's not a keyword search
            // so log as recommendations panel
            log_name = CLICK_LOG_TYPES.RECENT_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.RECENT_RECOMMENDATIONS;
        } else if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.FAVORITE_VISUALS
        ) {
            // if search term is blank, it's not a keyword search
            // so log as recommendations panel
            log_name = CLICK_LOG_TYPES.FAVORITE_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.FAVORITE_RECOMMENDATIONS;
        } else if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.UPLOADED_VISUALS
        ) {
            // if search term is blank, it's not a keyword search
            // so log as recommendations panel
            log_name = CLICK_LOG_TYPES.UPLOADS_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.UPLOADS_RECOMMENDATIONS;
        } else
            switch (search_context) {
                case SEARCH_CONTEXT.RECOMMENDED_VISUALS: {
                    log_name = CLICK_LOG_TYPES.MEDIA_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.MEDIA_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.RECENT_VISUALS: {
                    log_name = CLICK_LOG_TYPES.RECENT_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.RECENT_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.FAVORITE_VISUALS: {
                    log_name = CLICK_LOG_TYPES.FAVORITE_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.FAVORITE_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.UPLOADED_VISUALS: {
                    log_name = CLICK_LOG_TYPES.UPLOADS_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.UPLOADS_SEARCH;

                    break;
                }
                // No default
            }
        if (log_name !== null) {
            dispatch(
                logClick(
                    getVideoIdOrThrow(getState()),
                    {
                        type: assetType,
                        uuid: checkAssetUUID(uuid),
                    },
                    getVideoLanguageOrThrow(getState()),
                    {
                        log_name,
                    },
                ),
            );
            if (virtual_page !== null) {
                // don't connect to site search for recommendation panel events
                if (active_media_search_term !== '') {
                    analytics.trackSearchAction(`/${virtual_page}/click/?id=${uuid}`);
                }
                const clickDepth = media_asset_index + 1;
                analytics.trackSearchClick(virtual_page, clickDepth);
            }
        }
    };
}

export function logDragFromBackgroundTab(asset: ImmutableMap<AssetContainerModel>, el: Element) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        // if asset requires an upgrade it's an upsell so don't log
        const { assetType, source, uuid } = getClickLogInfo(asset);
        const badge = PremiumMediaChecker.badge_required_for_source(
            source,
            assetType === AssetTypes.ICON
                ? AssetContainerTypes.ICON
                : AssetContainerTypes.BACKGROUND,
        );
        if (
            badge === PREMIUM_LEVEL.UPGRADE_FOR_MARKETPLACE ||
            badge === PREMIUM_LEVEL.UPGRADE_FOR_UNLIMITED
        ) {
            return;
        }

        const search_context = getActiveSearchContextForCurrentTab(getState());
        if (!search_context) throw new Error('No search context found for current tab');
        const active_media_search_term = getActiveSearchTerm(getState(), search_context);

        // log the drag!
        let log_name = null;
        let virtual_page = null;
        if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.RECOMMENDED_VISUALS
        ) {
            // if search term is blank, it's not a keyword search so log
            // as recommendations panel
            log_name = CLICK_LOG_TYPES.MEDIA_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.MEDIA_RECOMMENDATIONS;
        } else if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.RECENT_VISUALS
        ) {
            // if search term is blank, it's not a keyword search so log
            // as recommendations panel
            log_name = CLICK_LOG_TYPES.RECENT_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.RECENT_RECOMMENDATIONS;
        } else if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.FAVORITE_VISUALS
        ) {
            // if search term is blank, it's not a keyword search so log
            // as recommendations panel
            log_name = CLICK_LOG_TYPES.FAVORITE_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.FAVORITE_RECOMMENDATIONS;
        } else if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.UPLOADED_VISUALS
        ) {
            // if search term is blank, it's not a keyword search
            // so log as recommendations panel
            log_name = CLICK_LOG_TYPES.UPLOADS_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.UPLOADS_RECOMMENDATIONS;
        } else
            switch (search_context) {
                case SEARCH_CONTEXT.RECOMMENDED_VISUALS: {
                    log_name = CLICK_LOG_TYPES.MEDIA_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.MEDIA_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.RECENT_VISUALS: {
                    log_name = CLICK_LOG_TYPES.RECENT_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.RECENT_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.FAVORITE_VISUALS: {
                    log_name = CLICK_LOG_TYPES.FAVORITE_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.FAVORITE_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.UPLOADED_VISUALS: {
                    log_name = CLICK_LOG_TYPES.UPLOADS_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.UPLOADS_SEARCH;

                    break;
                }
                // No default
            }
        if (log_name !== null) {
            dispatch(
                logDrag(
                    getVideoIdOrThrow(getState()),
                    {
                        type: assetType,
                        uuid: checkAssetUUID(uuid),
                    },
                    getVideoLanguageOrThrow(getState()),
                    {
                        log_name,
                    },
                ),
            );
            if (virtual_page !== null) {
                // don't connect to site search for recommendation panel events
                if (active_media_search_term !== '') {
                    analytics.trackSearchAction(`/${virtual_page}/drag/?id=${uuid}`);
                }
                const media_index_str = el.getAttribute('data-dragula-media-index');
                if (media_index_str === null) throw new Error('No media index found');
                const media_index = parseInt(media_index_str);
                if (!Number.isNaN(media_index) && media_index > -1) {
                    const clickDepth = media_index + 1;
                    analytics.trackSearchDrag(virtual_page, clickDepth);
                    if (clickDepth <= 10) {
                        analytics.trackSearchDragFirstPage(virtual_page, clickDepth);
                    }
                }
            }
        }
    };
}

export function logApplyFromBackgroundTab(
    asset: ImmutableMap<AssetContainerModel>,
    media_asset_index: number,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const { assetType, source, uuid } = getClickLogInfo(asset);

        // if asset requires an upgrade it's an upsell so don't log
        const badge = PremiumMediaChecker.badge_required_for_source(
            source,
            assetType === AssetTypes.ICON
                ? AssetContainerTypes.ICON
                : AssetContainerTypes.BACKGROUND,
        );
        if (
            badge === PREMIUM_LEVEL.UPGRADE_FOR_MARKETPLACE ||
            badge === PREMIUM_LEVEL.UPGRADE_FOR_UNLIMITED
        ) {
            return;
        }

        const search_context = getActiveSearchContextForCurrentTab(getState());
        if (!search_context) throw new Error('No search context found for current tab');
        const active_media_search_term = getActiveSearchTerm(getState(), search_context);

        // log the drag!
        let log_name = null;
        let virtual_page = null;
        if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.RECOMMENDED_VISUALS
        ) {
            // if search term is blank, it's not a keyword search so log
            // as recommendations panel
            log_name = CLICK_LOG_TYPES.MEDIA_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.MEDIA_RECOMMENDATIONS;
        } else if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.RECENT_VISUALS
        ) {
            // if search term is blank, it's not a keyword search so log
            // as recommendations panel
            log_name = CLICK_LOG_TYPES.RECENT_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.RECENT_RECOMMENDATIONS;
        } else if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.FAVORITE_VISUALS
        ) {
            // if search term is blank, it's not a keyword search so log
            // as recommendations panel
            log_name = CLICK_LOG_TYPES.FAVORITE_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.FAVORITE_RECOMMENDATIONS;
        } else if (
            active_media_search_term === '' &&
            search_context === SEARCH_CONTEXT.UPLOADED_VISUALS
        ) {
            // if search term is blank, it's not a keyword search
            // so log as recommendations panel
            log_name = CLICK_LOG_TYPES.UPLOADS_RECOMMENDATIONS;
            virtual_page = SITE_SEARCH_TYPES.UPLOADS_RECOMMENDATIONS;
        } else
            switch (search_context) {
                case SEARCH_CONTEXT.RECOMMENDED_VISUALS: {
                    log_name = CLICK_LOG_TYPES.MEDIA_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.MEDIA_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.RECENT_VISUALS: {
                    log_name = CLICK_LOG_TYPES.RECENT_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.RECENT_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.FAVORITE_VISUALS: {
                    log_name = CLICK_LOG_TYPES.FAVORITE_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.FAVORITE_SEARCH;

                    break;
                }
                case SEARCH_CONTEXT.UPLOADED_VISUALS: {
                    log_name = CLICK_LOG_TYPES.UPLOADS_SEARCH;
                    virtual_page = SITE_SEARCH_TYPES.UPLOADS_SEARCH;

                    break;
                }
                // No default
            }

        if (log_name !== null) {
            dispatch(
                logApply(
                    getVideoIdOrThrow(getState()),
                    {
                        type: assetType,
                        uuid: checkAssetUUID(uuid),
                    },
                    getVideoLanguageOrThrow(getState()),
                    {
                        log_name,
                    },
                ),
            );
            if (virtual_page !== null) {
                // don't connect to site search for recommendation panel events
                if (active_media_search_term !== '') {
                    analytics.trackSearchAction(`/${virtual_page}/apply/?id=${uuid}`);
                }
                if (!Number.isNaN(media_asset_index) && media_asset_index > -1) {
                    const clickDepth = media_asset_index + 1;
                    analytics.trackSearchApply(virtual_page, clickDepth);
                    if (clickDepth <= 10) {
                        analytics.trackSearchApplyFirstPage(virtual_page, clickDepth);
                    }
                }
            }
        }
    };
}
